import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

export default function UpgradeTooltip({ children }) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const tooltipRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const hideTimer = useRef(null);

  const open = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setShow(true);
  }, []);

  const closeWithDelay = useCallback((ms = 120) => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShow(false), ms);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = ref.current?.contains(event.target);
      const inTooltip = tooltipRef.current?.contains(event.target);
      if (!inTrigger && !inTooltip) setShow(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (show && ref.current && tooltipRef.current) {
      const rect = ref.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const GAP = 8; // positive gap feels nicer and avoids overlap jitter
      let top, left;

      // Prefer below, else above
      if (rect.bottom + tooltipRect.height + GAP <= viewportHeight) {
        top = rect.bottom + GAP;
      } else {
        top = Math.max(8, rect.top - tooltipRect.height - GAP);
      }

      // Prefer left-aligned to trigger’s left, clamp to viewport
      left = Math.min(
        Math.max(8, rect.left),
        viewportWidth - tooltipRect.width - 8
      );

      setCoords({ top, left });
    }
  }, [show]);

  useEffect(() => {
    return () => hideTimer.current && clearTimeout(hideTimer.current);
  }, []);

  return (
    <div
      className="relative inline-block"
      ref={ref}
      onMouseEnter={open}
      onMouseLeave={() => closeWithDelay()}
    >
      {children}

      {show &&
        createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-[9999] w-80 bg-white rounded-lg shadow-xl p-4 text-center"
            style={{ top: `${coords.top}px`, left: `${coords.left}px` }}
            // Keep open when hovering tooltip
            onMouseEnter={open}
            onMouseLeave={() => closeWithDelay()}
          >
            <div className="text-black font-semibold mb-2">
              Access to this particular feature requires an upgrade.
            </div>

            <div className="flex justify-center my-4">
              <img
                src="/images/promotions/upgrade-siemba.svg"
                alt="Feature icons"
                className="w-36 h-36 pointer-events-none select-none"
              />
            </div>
            <p className="text-sm mb-4">
              Ready to elevate your capabilities? <br />
              Please contact our sales team to get started!
            </p>
            <a
              href="https://www.siemba.io/book-a-demo"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block font-semibold border border-primary-500 text-primary-500 px-4 py-2 rounded hover:bg-primary-500 hover:text-white"
            >
              Contact Sales
            </a>
          </div>,
          document.body
        )}
    </div>
  );
}
